import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./full-fleet.scss";
import Header from "../_Common/header";
import SectionHead from "../_Common/SectionHead";
import useSeo from "../../hooks/useSeo";
import { Col, Container, Image, Nav, Row, Tab } from "react-bootstrap";

import { fleetTabsData } from "../../json/FullFleet/fleet-tabs.js";
import FleetService from "../../services/fleetService";

import sailingIcon from "../../assets/img/home/our-fleet/icons/sailing-boat.png";
import boatIcon from "../../assets/img/home/our-fleet/icons/boat.png";
import starIcon from "../../assets/img/home/our-fleet/icons/star.png";
import lightingIcon from "../../assets/img/home/our-fleet/icons/lighting.png";

import sailBoatsImg from "../../assets/img/home/our-fleet/sail-boats.png";
import sailYachtsImg from "../../assets/img/home/our-fleet/sail-yachts.png";
import motorYachtsImg from "../../assets/img/home/our-fleet/motor-yachts.png";
import speedBoatsImg from "../../assets/img/home/our-fleet/speed-boats.png";

import Aos from "aos";

export default function FullFleet() {
  useSeo({
    title: "Our Fleet: Yachts, Sail & Speed Boats in Mumbai | Ocean Tiger",
    description:
      "Browse sail boats, sail yachts, motor yachts, speed boats and a party ferry on charter from Gateway of India, Mumbai. Compare capacity and trips, then book.",
    canonical: "https://yachtmumbai.in/full-fleet",
  });

  const fleetImages = {
    sailBoats: sailBoatsImg,
    sailYachts: sailYachtsImg,
    motorYachts: motorYachtsImg,
    speedBoats: speedBoatsImg,
    ferryBoats: "/assets/ferry-boat/party-ferry-boat-feature-image.webp",
  };

  const [aosKey, setAosKey] = useState(0);
  const [activeTabKey, setActiveTabKey] = useState("sail_boats_tab");
  const [fleetData, setFleetData] = useState([]);
  const [isSticky, setIsSticky] = useState(false); // Sticky state
  const fleetGridRef = useRef(null); // Ref for scrolling
  const sentinelRef = useRef(null); // Ref for sticky sentinel
  const location = useLocation();
  const navigate = useNavigate();

  // Read URL params for active tab
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");

    // Check if category exists in our data keys
    const isValidCategory = categoryParam && FleetService.getValidTabKeys().includes(categoryParam);

    if (isValidCategory) {
      setActiveTabKey(categoryParam);
      // Wait for render then scroll
      setTimeout(() => {
        fleetGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      window.scrollTo(0, 0); // Default behavior
    }
  }, [location.search]); // Depend on search params

  // Load data when active tab changes
  useEffect(() => {
    const data = FleetService.getFleetCategory(activeTabKey);
    setFleetData(data);
  }, [activeTabKey]);

  // Sticky Navigation Observer... (keep as is)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: "-80px 0px 0px 0px" }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) observer.unobserve(sentinelRef.current);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      Aos.refreshHard();
    }, 100);
  }, [aosKey]);

  const handleTabClick = (key) => {
    setAosKey((prev) => prev + 1);
    setActiveTabKey(key);
    // Update URL to preserve state
    navigate(`/full-fleet?category=${key}`, { replace: true });
  };

  const activeTab = fleetTabsData.find((t) => t.key === activeTabKey);
  const activeTabName = activeTab ? activeTab.name : "Vessels";
  const activeTabSingular = activeTabName.replace(/s$/, "");

  // Direct Booking Logic
  const handleBookNow = (yacht) => {
    const amount = 2000;
    const yachtName = encodeURIComponent(yacht.name);
    const razorpayUrl = `https://pages.razorpay.com/yachtmumbai?amount=${amount}&yacht_name=${yachtName}`;
    // Google Ads conversion (defined in public/index.html). No URL is passed:
    // Razorpay opens in a new tab, and window.open must stay synchronous in
    // the click handler or popup blockers stop it.
    if (typeof window.gtag_report_conversion === "function") {
      window.gtag_report_conversion();
    }
    window.open(razorpayUrl, "_blank");
  };

  const handleViewDetails = (boat) => {
    const slug = boat.name.toLowerCase().replace(/\s+/g, '-');
    // Pass current active category as param
    navigate(`/fleet-detail/${slug}?category=${activeTabKey}`);
  };

  return (
    <React.Fragment>
      <Header />
      {/* FLEET HERO */}
      <section className="fleet-hero">
        <Container>
          <SectionHead
            as="h1"
            eyebrow="Premium Fleet Collection"
            title="Our Fleet"
            lead="From intimate sailboats to luxury motor yachts, discover the perfect vessel for your Mumbai maritime experience"
          />
        </Container>
      </section>

      {/* FLEET NAV TAB SECTION */}
      <section className="fleet-nav-tab-section">
        <Tab.Container
          id="left-tabs-example"
          activeKey={activeTabKey}
          onSelect={handleTabClick}
        >
          {/* Sentinel for sticky detection */}
          <div ref={sentinelRef} style={{ height: "1px", width: "100%", visibility: "hidden" }}></div>
          <div className={`fleet-tabs-header ${isSticky ? "is-sticky" : ""}`}>
            <Container>
              <Nav variant="pills">
                {fleetTabsData.map((item) => (
                  <Nav.Item key={item.id}>
                    <Nav.Link eventKey={item.key}>
                      <Image
                        src={
                          item.id === "01"
                            ? sailingIcon
                            : item.id === "02"
                              ? boatIcon
                              : item.id === "03"
                                ? starIcon
                                : item.id === "05"
                                  ? boatIcon
                                  : lightingIcon
                        }
                        alt=""
                      />
                      <span>{item.name}</span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Container>
          </div>
        </Tab.Container>
      </section>

      {/* FLEET GRID SECTION */}
      <section className="fleet-grid-section" ref={fleetGridRef}>
        <Container>
          <div className="fleet-grid-head">
            <h2>Our collection of {activeTabName}</h2>
            <span className="fleet-grid-count">{String(fleetData.length).padStart(2, "0")} vessels</span>
          </div>
          <Row className="g-3 g-lg-4">
            {fleetData.map((boat, idx) => (
              <Col lg={4} md={6} xs={12} key={boat.name} data-aos="fade-up" data-aos-delay={(idx % 3) * 80}>
                <article className="fleet-grid-card">
                  <button type="button" className="card-img-wrapper" onClick={() => handleViewDetails(boat)} aria-label={`View ${boat.name} details`}>
                    <Image
                      src={boat.image}
                      alt={`${boat.name} on charter at Gateway of India, Mumbai`}
                      loading="lazy"
                    />
                  </button>
                  <div className="card-content">
                    <div className="card-meta">
                      <span>{activeTabSingular}</span>
                      <span className="capacity-info">
                        <i className="ri-group-line"></i>
                        {boat.guestCount}
                      </span>
                    </div>
                    <h3>{boat.name}</h3>
                    <p>{boat.description}</p>
                    <dl className="trip-info">
                      <dt>Trip</dt>
                      <dd>
                        {Array.isArray(boat.trip) ? boat.trip.map((t, i) => (
                          <span key={i} className="trip-text">{t}</span>
                        )) : <span className="trip-text">{boat.trip}</span>}
                      </dd>
                    </dl>
                    <div className="card-actions">
                      <button type="button" className="btn-line" onClick={() => handleViewDetails(boat)}>
                        View Details
                      </button>
                      <button type="button" className="btn-ink" onClick={() => handleBookNow(boat)}>
                        Book Now
                      </button>
                    </div>
                  </div>
                </article>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </React.Fragment>
  );
}
